/**
 * Patient/study info data object
 */
import {PATIENT_ACTION} from "../../const";

export class PatientStudyInfoDataObject {
	/**
	 * @param {string} patientRefId - Patient's reference ID (read-only).
	 * @param {string} [patientId] - Patient's ID. This is only used for update when user selects an existing patient.
	 * @param {string} patientFirstName - Patient's first name.
	 * @param {string} [patientMiddleName=''] - Patient's middle name.
	 * @param {string} patientLastName - Patient's last name.
	 * @param {Date} patientDateOfBirth - Patient's birthdate.
	 * @param {?Gender} patientGender - Patient's gender.
	 * @param {string} [patientOptionalId] - Patient's ID gender.
	 * @param {PatientAction} [patientAction='UPDATE'] - Action to perform on patient if user changed patient info.
	 * @param {string} studyTypeId - Study type ID.
	 * @param {string} [studyNight1=''] - First study night memo.
	 * @param {string} [studyNight2=''] - Second study night memo.
	 * @param {string} [studyNight3=''] - Third study night memo.
	 * @param {string} [physicianId=''] - Ordering physician's ID. If empty, new ordering physician will be created using
	 * the 'newPhysician' param.
	 * @param {{credentials: string, firstName: string, lastName: string}|null} [newPhysician=null] - Params of the new 
	 * physician if one should be crated ('physicianId' must be empty).
	 * @param {PatientStudyInfoManualDateDataObject[]} [manualDates=[]] - Manually set study recorded dates.
	 * @param {Date} patientMaxDateOfBirth - (read-only) Date after witch patient birthdate should display a waring 
	 * message for underage patients (less than 18 years old).
	 * @param {boolean} [yearWarning=false] - (read-only) Flag that specifies if patient exceeds the 
	 * 'patientMaxDateOfBirth' (less than 18 years old). If this flag is returned as true on IO update, warning message 
	 * should appear after update success message.
	 */
	constructor(
		patientRefId, patientId, patientFirstName, patientMiddleName = '', patientLastName, patientDateOfBirth, 
		patientGender, patientOptionalId = '', patientAction = PATIENT_ACTION.UPDATE, studyTypeId, studyNight1 = '', 
		studyNight2 = '', studyNight3 = '', physicianId, newPhysician = null, manualDates = [], patientMaxDateOfBirth, 
		yearWarning = false,
	) {
		this.patientRefId = patientRefId;
		this.patientId = patientId;
		this.patientFirstName = patientFirstName;
		this.patientMiddleName = patientMiddleName;
		this.patientLastName = patientLastName;
		this.patientDateOfBirth = patientDateOfBirth;
		this.patientGender = patientGender;
		this.patientOptionalId = patientOptionalId;
		this.patientAction = patientAction;
		this.studyTypeId = studyTypeId; // API study/fetch-study-types
		this.studyNight1 = studyNight1;
		this.studyNight2 = studyNight2;
		this.studyNight3 = studyNight3;
		this.physicianId = physicianId; // API study/fetch-ordering-physicians-by-study
		this.newPhysician = newPhysician;
		this.manualDates = manualDates;
		this.patientMaxDateOfBirth = patientMaxDateOfBirth;
		this.yearWarning = yearWarning;
	}
}

export class PatientStudyInfoManualDateDataObject {
	/**
	 * @param {string} id - ID of the item in the list.
	 * @param {string} studyId - ID of teh study.
	 * @param {string} [savedDate=''] - (read-only) Original study recorded date.
	 * @param {string} [manualDate=''] - Manually set study recorded date (overwrites original).
	 * @param {Date} [manualDateFormatted] - Formatted date object for manual date (new functionality to replace the 
	 * 'manualDate').
	 * @param {string} [manualTimeH=''] - Manually set study recorded hour.
	 * @param {string} [manualTimeM=''] - Manually set study recorded minute.
	 * @param {number} [ordinal=0] - Ordinal of the item in the list.
	 */
	constructor(
		id, studyId, savedDate = '', manualDate = '', manualDateFormatted = null, manualTimeH = '', manualTimeM = '',
		ordinal = 0
	) {
		this.id = id;
		this.studyId = studyId;
		this.savedDate = savedDate;
		this.manualDate = manualDate;
		this.manualDateFormatted = manualDateFormatted;
		this.manualTimeH = manualTimeH;
		this.manualTimeM = manualTimeM;
		this.ordinal = ordinal;
	}
}